import { Button, Col, Modal, Row } from "react-bootstrap";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../store/hooks";
import { selectedOrganization } from "../../../store/organizationsSlice";

// Step 0 of flight creation: the flight rules decide the whole shape of the flow (route entry,
// levels, alternates, filing path), so the user picks VFR or IFR before the form opens.
// IFR is an org-level opt-in (organization.ifrEnabled, default off) — without it there is no
// choice to make, so /flights/add goes straight to the VFR form.
// Rendered as a MODAL over the flights list (this component sits on the /flights/add route, which
// react-router renders inside the list page's outlet — a bare page there would float over the list
// without any backdrop). Closing navigates back to /flights.
// Query params (e.g. airfield-slot deep links) are forwarded onto the chosen path.
function AddFlightChooserPage() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const selectedOrg = useAppSelector(selectedOrganization);

  if (!selectedOrg?.ifrEnabled) {
    return <Navigate to={`/flights/add/vfr${search}`} replace />;
  }

  const choose = (mode: "vfr" | "ifr") => {
    navigate(`/flights/add/${mode}${search}`);
  };

  const close = () => {
    navigate("/flights");
  };

  return (
    <Modal show onHide={close} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>New Flight</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <div className="text-center text-muted mb-4">How will this flight be conducted?</div>

        <Row className="g-3">
          <Col xs={12} md={4}>
            <RulesCard
              code="VFR"
              title="Visual Flight Rules"
              points={["Draw your own route with waypoints", "Any altitude, no alternate required", "Filed to the route's AFTN addresses"]}
              onSelect={() => choose("vfr")}
            />
          </Col>
          <Col xs={12} md={4}>
            <RulesCard
              code="IFR"
              title="Instrument Flight Rules"
              points={["EUROCONTROL-validated airway route", "Flight level, alternate & performance data", "Filed to IFPS (EUROCONTROL)"]}
              onSelect={() => choose("ifr")}
            />
          </Col>
          <Col xs={12} md={4}>
            <RulesCard
              code="Y/Z"
              title="Mixed VFR / IFR"
              points={["Depart VFR, pick up IFR en route (Z) — or the reverse (Y)", "IFR portion EUROCONTROL-validated", "Filed to IFPS + the VFR ATS units"]}
              onSelect={() => navigate(`/flights/add/ifr${search ? search + "&" : "?"}rules=Z`)}
              actions={[
                { title: "Depart VFR → IFR (Z)", onClick: () => navigate(`/flights/add/ifr${search ? search + "&" : "?"}rules=Z`) },
                { title: "Depart IFR → VFR (Y)", onClick: () => navigate(`/flights/add/ifr${search ? search + "&" : "?"}rules=Y`) },
              ]}
            />
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

interface RulesCardProps {
  code: string;
  title: string;
  points: string[];
  onSelect: () => void;
  buttonTitle?: string;
  //when set, the card renders these buttons instead of the single one (e.g. Mixed → Z / Y)
  actions?: { title: string; onClick: () => void }[];
}

function RulesCard({ code, title, points, onSelect, buttonTitle, actions }: RulesCardProps) {
  return (
    <div
      className="border rounded-3 p-4 h-100 d-flex flex-column"
      role={actions ? undefined : "button"}
      onClick={actions ? undefined : onSelect}
      style={{ cursor: "pointer", transition: "box-shadow 0.15s ease, transform 0.15s ease" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.12)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "none";
      }}
    >
      <div className="d-flex align-items-center gap-3 mb-3">
        <span
          className="badge rounded-2 d-inline-flex align-items-center justify-content-center"
          style={{
            width: "64px",
            height: "44px",
            fontSize: "1.05rem",
            letterSpacing: "0.06em",
            //same colors as the rules pills in the flights list: VFR green, IFR red, mixed = both
            background:
              code === "IFR"
                ? "#dc3545"
                : code === "VFR"
                ? "#198754"
                : "linear-gradient(135deg, #198754 50%, #dc3545 50%)",
          }}
        >
          {code}
        </span>
        <span className="fw-semibold fs-5">{title}</span>
      </div>
      <ul className="text-muted small mb-4 ps-3" style={{ lineHeight: 1.9 }}>
        {points.map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ul>
      <div className="d-grid gap-2 mt-auto">
        {actions ? (
          actions.map((a) => (
            <Button key={a.title} variant="dark" onClick={a.onClick}>
              {a.title}
            </Button>
          ))
        ) : (
          <Button variant="dark" onClick={onSelect}>
            {buttonTitle ?? `Create ${code} flight`}
          </Button>
        )}
      </div>
    </div>
  );
}

export default AddFlightChooserPage;
